import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import { company, isString, json, record } from '../data/members'
import { request, type Axis, type WriteOutcome } from '../hypotheses/dictionaryApi'
import { periodQuery, type Period } from '../lib/period'
import type { CreativesResponse, CreativeDetailResponse } from './contract'
import {
  parseCreativeDetailResponse,
  parseCreativesResponse,
  readErrorBody,
  UNKNOWN_CREATIVE_ERROR_CODE,
} from './contract'

/** The creative list contract, served from the company's stored rows. */
export const CREATIVES_URL = '/api/creatives'

const TIMEOUT_MS = 10_000

/** The request deadline, exported so callers composing their own abort signal with it
 * keep the same window instead of overriding it. */
export const REQUEST_TIMEOUT_MS = TIMEOUT_MS

/** What a request answers with when its session ended while it was in flight. */
function authInvalidated(): DOMException {
  return new DOMException('auth session invalidated', 'AbortError')
}

/**
 * A refusal the shell has to fix, not one a retry can.
 *
 * `mixed_provenance`, `mixed_currency` and `unknown_account` mean the request named
 * accounts the company cannot be asked about together, or at all; the screen cannot change the selection and
 * says which accounts are at fault instead of offering a retry that would fail the same
 * way.
 */
export class CreativesRequestError extends Error {
  readonly code: string
  readonly accounts: string[]

  constructor(code: string, message: string, accounts: string[]) {
    super(message)
    this.name = 'CreativesRequestError'
    this.code = code
    this.accounts = accounts
  }
}

export interface CreativesParams {
  /** The company the request reads. Required on every request, and part of every
   * response's identity: the server answers `404` for a company the caller is not in. */
  companyId: string
  period: Period
  /** Ask for the previous window of the same length. The detail route never carries one. */
  compare?: boolean
  /** `null` is the company's default scope. Never an empty array: see below. */
  accountIds: string[] | null
  audienceId: string | null
  /** The instant to evaluate at, the `as_of` a previous response echoed. Only the
   * hypotheses round sends it, so every request of the round is classified at one time. */
  asOf?: string | null
}

function url(params: CreativesParams): string {
  const query = queryOf(params)
  return `${CREATIVES_URL}?${query.toString()}`
}

function queryOf(params: CreativesParams): URLSearchParams {
  const query = new URLSearchParams({ company: params.companyId, ...periodQuery(params.period) })
  if (params.compare) query.set('compare', 'previous')
  // `accounts=` present and empty is a 400 by contract, never "everything", so an empty
  // selection must not reach the wire at all. The screen renders its no-selection state
  // without a request; this guard is the second line, for a caller that forgets.
  if (params.accountIds !== null) {
    if (params.accountIds.length === 0) {
      throw new Error('fetchCreatives was called with an empty account selection')
    }
    query.set('accounts', params.accountIds.join(','))
  }
  if (params.audienceId !== null) query.set('audience', params.audienceId)
  if (params.asOf) query.set('as_of', params.asOf)
  return query
}

/** A 404 from the detail route: the creative is out of scope or gone. The panel renders its
 * not-found state; a retry re-asks, a close dismisses. */
export class CreativeDetailUnknownError extends Error {
  constructor() {
    super('no in-scope ad carries this creative id')
    this.name = 'CreativeDetailUnknownError'
  }
}

/**
 * Resolves with the parsed response, or throws with a message that says which step failed.
 *
 * The generation this request was issued under is captured before it goes out and checked
 * twice: once when the response arrives, and again after its body has been read, because a
 * body that is already being read when the session ends still finishes reading. Work from a
 * disposed session is then discarded rather than delivered - no rows reach the caller, and,
 * just as importantly, a 401 that belongs to a session already gone does not report the
 * current one as expired. Discarded work rejects with an `AbortError`, the same result an
 * aborted request gives, which callers already ignore; nothing is left permanently pending.
 */
/**
 * The list's `404 not_found`: the company does not exist or the caller is not a member of
 * it. The shell re-checks the session before it believes either (GRO-106, "Lost access").
 */
export class CompanyNotFoundError extends Error {
  constructor() {
    super('the company is not in this session')
    this.name = 'CompanyNotFoundError'
  }
}

function companyNotFound(body: unknown): Error | null {
  const error = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined
  return error === 'not_found' ? new CompanyNotFoundError() : null
}

export async function fetchCreatives(
  params: CreativesParams,
  fetchImpl: typeof fetch = fetch,
  signal: AbortSignal = AbortSignal.timeout(TIMEOUT_MS),
): Promise<CreativesResponse> {
  return parseCreativesResponse(await send(url(params), fetchImpl, signal, companyNotFound))
}

/**
 * The detail route: the creative id in the path segment, the list's query on the line.
 * `split=delivered` asks the server to leave out the accounts observed to deliver nothing;
 * a server that predates it ignores the parameter and returns every account.
 */
export const CREATIVE_DETAIL_URL = `${CREATIVES_URL}`

function detailUrl(id: string, params: CreativesParams): string {
  const query = queryOf({ ...params, compare: false })
  query.set('split', 'delivered')
  return `${CREATIVE_DETAIL_URL}/${id}?${query.toString()}`
}

export async function fetchCreativeDetail(
  id: string,
  params: CreativesParams,
  fetchImpl: typeof fetch = fetch,
  signal: AbortSignal = AbortSignal.timeout(TIMEOUT_MS),
): Promise<CreativeDetailResponse> {
  return parseCreativeDetailResponse(
    await send(detailUrl(id, params), fetchImpl, signal, (body) => {
      const error = readErrorBody(body)
      return error?.code === UNKNOWN_CREATIVE_ERROR_CODE ? new CreativeDetailUnknownError() : null
    }),
  )
}
/**
 * The request both contracts share: the session-generation guard, the 401 expiry report,
 * the 400 code reading, the JSON check and the timeout. Resolves with the raw body, or
 * throws with a message that says which step failed.
 *
 * The generation this request was issued under is captured before it goes out and checked
 * twice: once when the response arrives, and again after its body has been read, because a
 * body that is already being read when the session ends still finishes reading. Work from a
 * disposed session is then discarded rather than delivered - no rows reach the caller, and,
 * just as importantly, a 401 that belongs to a session already gone does not report the
 * current one as expired. Discarded work rejects with an `AbortError`, the same result an
 * aborted request gives, which callers already ignore; nothing is left permanently pending.
 * `onNotFound`, when given, inspects a 404's body and returns the caller's own error for
 * it - the detail route is the only one that can answer 404, and the panel renders the
 * `unknown_creative` code as a state, not as a failure; any other 404 falls through to the
 * generic failure, because "the route is off" is not "the creative is out of scope".
 */
async function send(
  target: string,
  fetchImpl: typeof fetch,
  signal: AbortSignal,
  onNotFound?: (body: unknown) => Error | null,
): Promise<unknown> {
  const generation = authGeneration()
  const combined = AbortSignal.any([signal, authSignal()])
  const response = await fetchImpl(target, {
    signal: combined,
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (generation !== authGeneration()) {
    throw authInvalidated()
  }
  if (response.status === 401) {
    reportSessionExpired()
    throw authInvalidated()
  }

  let body: unknown
  let parsedBody = true
  try {
    body = await response.json()
  } catch {
    parsedBody = false
  }
  if (generation !== authGeneration()) {
    throw authInvalidated()
  }

  // A 400 carries a code saying what about the request was wrong. It is read before the
  // status check below so the screen can tell "the shell asked for an account this company
  // does not have" from "the API is down", which look the same from a status line alone.
  // A 409 is read the same way: `as_of_out_of_range` is a round the caller fetches again.
  if ((response.status === 400 || response.status === 409) && parsedBody) {
    const error = readErrorBody(body)
    if (error) {
      throw new CreativesRequestError(error.code, error.message, error.accounts ?? [])
    }
  }
  if (response.status === 404 && onNotFound) {
    const error = onNotFound(parsedBody ? body : undefined)
    if (error) {
      throw error
    }
  }
  if (!response.ok) {
    throw new Error(`${target} answered ${response.status}`)
  }
  if (!parsedBody) {
    throw new Error(`${target} did not return JSON`)
  }
  return body
}

/**
 * One assignment per axis (GRO-170). An absent field leaves the axis as it is, `null`
 * clears it and an id sets it.
 */
export interface TagChange {
  ideaId?: string | null
  visualId?: string | null
  messageId?: string | null
  offerId?: string | null
}

/** The `PUT /api/creatives/tags` field of each axis. */
const TAG_FIELD = { idea: 'ideaId', visual: 'visualId', message: 'messageId', offer: 'offerId' } as const

/** A change of one axis: its value id, or `null` to clear it. */
export function tagChange(axis: Axis, id: string | null): TagChange {
  return { [TAG_FIELD[axis]]: id }
}

/** The axes a change sets to a value; a cleared axis cannot be refused for its value. */
export function changedAxes(change: TagChange): Axis[] {
  return (Object.keys(TAG_FIELD) as Axis[]).filter((axis) => typeof change[TAG_FIELD[axis]] === 'string')
}

/**
 * `PUT /api/creatives/tags`: the idea, the visual or both on every listed creative group.
 * The ids are the list's creative ids. The server writes all of them or nothing, and
 * answers with the number of groups it updated.
 */
export function assignTags(
  companyId: string,
  creativeIds: string[],
  change: TagChange,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<{ updated: number }>> {
  return request(
    `${CREATIVES_URL}/tags?${company(companyId)}`,
    json('PUT', { creativeIds, ...change }),
    (body) => {
      const updated = record(body)?.updated
      return typeof updated === 'number' ? { updated } : null
    },
    fetchImpl,
  )
}

/**
 * `PUT /api/creatives/version`: one version letter, `A` to `Z`, on every listed creative
 * group, or none with `null`. Every group must carry a hypothesis. The server writes all of
 * them or nothing, and answers with the number of groups it updated.
 */
export function setVersion(
  companyId: string,
  creativeIds: string[],
  version: string | null,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<{ updated: number }>> {
  return request(
    `${CREATIVES_URL}/version?${company(companyId)}`,
    json('PUT', { creativeIds, version }),
    (body) => {
      const updated = record(body)?.updated
      return typeof updated === 'number' ? { updated } : null
    },
    fetchImpl,
  )
}

/** A hypothesis and its version letter, the assignment a merged creative keeps (GRO-161). */
export interface MergeKeep {
  hypothesisId: string
  version: string | null
}

/**
 * `POST /api/creatives/merge`: merges the listed creative groups into the one created
 * first and answers its id. `keep` names the assignment the merged row keeps when the
 * groups carry more than one; the server refuses one that is no longer among them.
 */
export function mergeCreatives(
  companyId: string,
  creativeIds: string[],
  keep: MergeKeep | null,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<{ creativeId: string }>> {
  return request(
    `${CREATIVES_URL}/merge?${company(companyId)}`,
    json('POST', keep === null ? { creativeIds } : { creativeIds, keep }),
    (body) => {
      const creativeId = record(body)?.creativeId
      return typeof creativeId === 'string' ? { creativeId } : null
    },
    fetchImpl,
  )
}

/** A reader's answer to an open suggestion (GRO-173). */
export type Decision = 'accept' | 'reject'

/**
 * How a decision ended: the classes of a write, plus the three refusals `request()` cannot
 * tell apart. `settled` and `axisAssigned` are the `409`s of a suggestion decided, or an
 * axis filled, since the read; `gone` is the `404` of a suggestion that no longer exists,
 * which is not a lost company.
 */
export type DecisionOutcome =
  | WriteOutcome<{ valueId: string | null }>
  | { status: 'settled' }
  | { status: 'axisAssigned' }
  | { status: 'gone' }

const DECISION_REFUSALS: Record<string, DecisionOutcome> = {
  suggestion_settled: { status: 'settled' },
  axis_assigned: { status: 'axisAssigned' },
  unknown_suggestion: { status: 'gone' },
}

/**
 * `POST /api/creatives/suggestions/{id}`: an accept assigns the suggested value, creating
 * it when the dictionary has none of that name, and answers its id; a reject answers no
 * body. The `422` codes come through `request()`; the `404` and `409` codes are read here.
 */
export async function decideSuggestion(
  companyId: string,
  id: string,
  decision: Decision,
  fetchImpl: typeof fetch = fetch,
): Promise<DecisionOutcome> {
  const seen: { refusal: DecisionOutcome | null } = { refusal: null }
  const observing: typeof fetch = async (input, init) => {
    const response = await fetchImpl(input, init)
    if (response.status === 404 || response.status === 409) {
      const code = record(await response.clone().json().catch(() => null))?.error
      seen.refusal = isString(code) ? (DECISION_REFUSALS[code] ?? null) : null
    }
    return response
  }
  const outcome = await request(
    `${CREATIVES_URL}/suggestions/${encodeURIComponent(id)}?${company(companyId)}`,
    json('POST', { decision }),
    (body) => {
      if (body === null) return { valueId: null }
      const valueId = record(body)?.valueId
      return isString(valueId) ? { valueId } : null
    },
    observing,
  )
  const refused = outcome.status === 'notFound' || outcome.status === 'unavailable'
  return refused && seen.refusal !== null ? seen.refusal : outcome
}
